import { toast } from '@heroui/react';
import { useState, useMemo } from 'react';

import type { SortingState, PaginationState } from '@hs/ui';
import { AlertDialog, Button, Dropdown, Label } from '@heroui/react';
import {
  IconPlus as Plus,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { ResourceEditorModal } from './ResourceEditorModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type Resource = {
  id: string;
  code?: string | null;
  name?: string | null;
  mode?: string | null;
  unit?: string | null;
  consumptionStrategy?: string | null;
  createdAt?: string;
};

const RESOURCE_SORT_FIELDS = new Set(['code', 'name', 'mode', 'unit']);

export function ResourcesManagement() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<Resource | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(null);
  const [deletingResourceId, setDeletingResourceId] = useState<string | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];
  const sortBy =
    sortItem && RESOURCE_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as 'code' | 'name' | 'mode' | 'unit')
      : undefined;

  const { data, isLoading, refetch } = trpc.bizResource.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortBy
        ? { sortBy, sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const resources = (data?.items ?? []) as Resource[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteResourceMutation = trpc.bizResource.delete.useMutation({
    onSuccess: () => {
      toast.success('资源删除成功');
      setDeletingResourceId(null);
      setDeleteModalOpen(false);
      setResourceToDelete(null);
      void utils.bizResource.list.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
    },
    onSettled: () => {
      setDeletingResourceId(null);
    },
  });

  const handleCreate = () => {
    setEditingResource(null);
    setEditorModalOpen(true);
  };

  const handleEdit = (resource: Resource) => {
    setEditingResource(resource);
    setEditorModalOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorModalOpen(false);
    setEditingResource(null);
    void refetch();
  };

  const handleDelete = (resource: Resource) => {
    setResourceToDelete(resource);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!resourceToDelete) return;
    setDeletingResourceId(resourceToDelete.id);
    try {
      await deleteResourceMutation.mutateAsync({ id: resourceToDelete.id });
    } catch {
      /* onError */
    }
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<Resource>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="resource_definition"
          data={resources}
          recordCount={total}
          pageCount={pageCount}
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <div className="flex flex-wrap items-center gap-2">
              <Button onPress={handleCreate}>
                <Plus className="size-4" stroke={1.5} />
                创建
              </Button>
              <Button variant="secondary" onPress={() => void refetch()}>
                <RefreshCw className="size-4" stroke={1.5} />
                刷新
              </Button>
            </div>
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<Resource>>
            {({ row }) => {
              const resource = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => handleEdit(resource)}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete"
                          textValue="删除"
                          variant="danger"
                          onAction={() => handleDelete(resource)}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <ResourceEditorModal
        open={editorModalOpen}
        resource={editingResource}
        onCancel={() => {
          setEditorModalOpen(false);
          setEditingResource(null);
        }}
        onSuccess={handleEditorSuccess}
      />

      <AlertDialog isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除资源「{resourceToDelete?.name ?? resourceToDelete?.code}」吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setResourceToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletingResourceId === resourceToDelete?.id}
                  onPress={() => void confirmDelete()}
                >
                  {deletingResourceId === resourceToDelete?.id ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
